import {useNavigate, useParams} from "react-router-dom";
import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {OptionType} from "../../../helpers/utils/StyleUtils";
import Breadcrumb, {Crumb} from "../../../components/common/Breadcrumbs";
import InputField from "../../../components/common/form/InputField";
import Form from "../../../components/common/form/Form";
import {useGetAllTenantsQuery} from "../../customer/tenantApi";
import SelectField from "../../../components/common/form/SelectField";
import {createOptionListForSelectTag} from "../../../helpers/utils/Utils";
import {hasPermission, jwtDecode} from "../../../helpers/utils/AccessControlUtils";
import {
    useCreateFollowUpTypeMutation,
    useUpdateFollowUpTypeIdMutation,
    useGetFollowUpTypeByIdQuery
} from "./followUpTypesApi";
import {handleRTKQuery} from "../../../helpers/utils/RtkQueryUtils";
import {skipToken} from "@reduxjs/toolkit/query";
import AuditableInfoButton from "../../../components/common/form/AuditableInfoButton";
import {Routes as AppRoutes} from "../../../routes";
import ButtonTray from "../../../components/common/form/ButtonTray";
import {teamsApi, useGetTeamByIdQuery, useGetTeamByTenantQuery} from "../../user/teams/teamsApi";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
}

const FollowUpTypeForm: FC<Props> = ({crumbs, title, btnText, isEditable = true}) => {
    const navigate = useNavigate();
    const {typeId} = useParams();
    const [followUpObj, setFollowUpObj] = useState<any>({
        hexColor: "#000000",
    });
    const {data: tenantData} = useGetAllTenantsQuery("");
    const {data: followUpData} = useGetFollowUpTypeByIdQuery(typeId ?? skipToken);
    const {data: teamsData} = useGetTeamByTenantQuery(followUpObj?.tenantId?.label ?? skipToken);


    const [createFollowUpTypeApi, createFollowUpTypeApiResponse] = useCreateFollowUpTypeMutation();
    const [updateFollowUpTypApi, updateFollowUpTypApiResponse] = useUpdateFollowUpTypeIdMutation();

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const {name, value} = "target" in e ? e.target : e;
        setFollowUpObj((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (followUpData) {
            setFollowUpObj({
                ...followUpData,
                tenantId: followUpData.tenant ? {value: followUpData.tenant, label: followUpData.tenant} : null,
                teamOnOpen: followUpData.teamOnOpen ? {
                    value: followUpData.teamOnOpen,
                    label: teamsData?.find((el: any) => el.id === followUpData.teamOnOpen)?.teamName || ""
                } : null,
                teamOnClose: followUpData.teamOnClose ? {
                    value: followUpData.teamOnClose,
                    label: teamsData?.find((el: any) => el.id === followUpData.teamOnClose)?.teamName || ""

                } : null,
            });
        }
    }, [followUpData, teamsData]);

    const handleToggle = (property: string) => () => {
        setFollowUpObj((prevData: any) => ({
            ...prevData,
            [property]: !prevData[property]
        }));
    };
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const payload = {
            ...followUpObj,
            tenantId: followUpObj?.tenantId?.value || null,
            teamOnOpen: followUpObj?.teamOnOpen?.value || null,
            teamOnClose: followUpObj?.teamOnClose?.value || null,
        };


        await handleRTKQuery(
            async () => {
                if (btnText !== "Update") {
                    return await createFollowUpTypeApi(payload).unwrap();
                } else {
                    return await updateFollowUpTypApi({id: typeId, requestBody: payload}).unwrap();
                }
            },
            (response: any) => {
                 if (response?.success || response?.data?.success) {
                    setFollowUpObj({hexColor: "#000000"});
                    navigate("/follow-up-types");
                }
            }
        );
    };

    return (
        <div className="adduser_container px-4 py-2">
            <div className="justify-between sm:block md:flex lg:flex">
                <Breadcrumb crumbs={crumbs}/>
                { hasPermission( ["AUDIT-V"]) &&
                    <AuditableInfoButton obj={followUpData}/>
                }
            </div>

            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                        {title}
                    </h3>
                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <div className="grid grid-cols-12 adduser_Wrapper">
                                <InputField
                                    name="name"
                                    label="Name"
                                    onChange={handleOnChange}
                                    value={followUpObj?.name || ""}
                                    type="text"
                                    placeholder="Enter Name..."
                                    required
                                />

                                <InputField
                                    name="hexColor"
                                    label="Color"
                                    onChange={handleOnChange}
                                    value={followUpObj?.hexColor || ""}
                                    type="color"
                                    required
                                    className="h-12 w-14"
                                />

                                {jwtDecode()?.customerLevel === "SYS" && (
                                    <SelectField
                                        placeholder="Select Tenant..."
                                        value={followUpObj?.tenantId || null}
                                        options={createOptionListForSelectTag(tenantData, "name", "id")}
                                        onChange={(selectedOption) => handleOnChange({
                                            name: "tenantId",
                                            value: selectedOption
                                        })}
                                        required
                                        label="Tenant"
                                    />
                                )}


                                <InputField
                                    name="closeOnCustomerUpload"
                                    label="Close On Customer Upload"
                                    checked={followUpObj?.closeOnCustomerUpload || ""}
                                    type="checkbox"
                                    onChange={handleToggle("closeOnCustomerUpload")}
                                    className="font-normal mt-5"
                                    labelClassName={'font-normal'}
                                />
                                <InputField
                                    name="assignOnOpen"
                                    label="Assign On Open"
                                    checked={followUpObj?.assignOnOpen || ""}
                                    type="checkbox"
                                    onChange={handleToggle("assignOnOpen")}
                                    className="font-normal mt-5"
                                    labelClassName={'font-normal'}
                                />

                                {followUpObj?.assignOnOpen && (
                                    <>
                                        <SelectField
                                            placeholder="Select Team..."
                                            value={followUpObj?.teamOnOpen || null}
                                            options={createOptionListForSelectTag(teamsData, "teamName", "id")}
                                            onChange={(selectedOption) =>
                                                handleOnChange({
                                                    name: "teamOnOpen",
                                                    value: selectedOption,
                                                })
                                            }
                                            required
                                            isDisabled={!teamsData}
                                            label="Team On Open"
                                        />
                                        <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                            <div
                                                className="col-span-3 sm:mt-2 lg-mt-0 mt-2 flex items-center font-normal undefined">

                                            </div>
                                            <div className="col-span-9 mt-3">
                                                {!followUpObj?.teamOnOpen && (
                                                    <p className="help-text mb-0 ">
                                                        If left blank, the product will be unassigned from the currently
                                                        assigned team, if applicable.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                    </>
                                )}


                                <InputField
                                    name="assignOnClose"
                                    label="Assign On Close"
                                    checked={followUpObj?.assignOnClose || ""}
                                    type="checkbox"
                                    onChange={handleToggle("assignOnClose")}
                                    className="font-normal mt-5"
                                    labelClassName={'font-normal'}
                                />
                                {followUpObj?.assignOnClose && (
                                    <>
                                        <SelectField
                                            placeholder="Select Team..."
                                            value={followUpObj?.teamOnClose || null}
                                            options={createOptionListForSelectTag(teamsData, "teamName", "id")}
                                            onChange={(selectedOption) =>
                                                handleOnChange({
                                                    name: "teamOnClose",
                                                    value: selectedOption,
                                                })
                                            }
                                            required
                                            isDisabled={!teamsData}
                                            label="Team On Close"
                                        />
                                        <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                            <div
                                                className="col-span-3 sm:mt-2 lg-mt-0 mt-2 flex items-center font-normal undefined">

                                            </div>
                                            <div className="col-span-9 mt-3">
                                                {!followUpObj?.teamOnClose && (
                                                    <p className="help-text mb-0 ">
                                                        If left blank, the product will be unassigned from the currently
                                                        assigned team, if applicable.
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>

                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>
                                        <ButtonTray buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => navigate(AppRoutes.FollowUpTypes.path),
                                                    type: "cancel"
                                                },
                                                buttonType: "button"
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",
                                                    type: "submit",
                                                    isLoading: (createFollowUpTypeApiResponse.isLoading || updateFollowUpTypApiResponse.isLoading),
                                                    isVisible: isEditable
                                                },
                                                buttonType: "button"
                                            }
                                        ]} align="center" gap={2}/>
                                    </div>
                                </div>
                            </div>
                        </>

                    }/>
                </div>
            </div>
        </div>
    );
};

export default FollowUpTypeForm;
